import { useApolloClient } from "@apollo/client";
import { Modal, ModalOpenButton } from "@msys/ui";
import { Box, Button, DialogContentText, Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import * as Sentry from "@sentry/react";
import { Form, Formik } from "formik";
import { uniqueId } from "lodash-es";
import { useSnackbar } from "notistack";
import React from "react";
import * as Yup from "yup";
import { RequestSupplierModal } from "../crm-companies/modals/RequestSupplierModal.js";
import { SearchSuppliers__SupplierFragment } from "./SupplierSelect.generated.js";
import {
  useCreateNewSupplierMutation,
  useSubscribeToSupplierMutation,
} from "./SupplierCreateModal.generated.js";
import { SupplierSelectMultiple } from "./SupplierSelectMultiple.js";

interface AddSupplierFormValues {
  pimSuppliers: SearchSuppliers__SupplierFragment[];
}

interface Props {
  handleClose: () => void;
  handleComplete: (handleClose: () => void) => void | Promise<void>;
}

export const SupplierCreateModal = ({ handleClose, handleComplete }: Props) => {
  const { t } = useTranslate([
    "SupplierNew",
    "Global",
    "Product",
    "OrganisationInvite",
  ]);
  const { enqueueSnackbar } = useSnackbar();

  const client = useApolloClient();

  const [subscribeToSupplier] = useSubscribeToSupplierMutation({
    client,
  });

  const [copySupplierOrganisationAsCrm] = useCreateNewSupplierMutation({
    client,
  });

  const subscribeToOneSupplier = React.useCallback(
    async (pimSupplierId: string) => {
      await subscribeToSupplier({
        variables: {
          input: {
            supplierId: pimSupplierId,
          },
        },
      });

      // The supplier is now subscribed (the user's goal). The CRM mirror is
      // best-effort — a failure here must not mark the supplier as "not added".
      try {
        const result = await copySupplierOrganisationAsCrm({
          variables: {
            input: {
              pimSupplierId: pimSupplierId,
            },
          },
        });
        if (!result.data?.copySupplierOrganisationAsCrm.crmOrganisation) {
          throw new Error("Failed to copy supplier organisation as CRM");
        }
      } catch (e) {
        Sentry.captureException(e);
      }
    },
    [subscribeToSupplier, copySupplierOrganisationAsCrm]
  );

  const onSubmit = React.useCallback(
    async (values: AddSupplierFormValues) => {
      const suppliers = values.pimSuppliers;
      if (suppliers.length === 0) return;

      // Subscriptions are independent: subscribe to each supplier and keep the
      // successes even if some fail, surfacing which ones did not go through.
      const results = await Promise.allSettled(
        suppliers.map(supplier => subscribeToOneSupplier(supplier.id))
      );

      const failedSuppliers = suppliers.filter(
        (_, index) => results[index]?.status === "rejected"
      );
      const succeededCount = suppliers.length - failedSuppliers.length;

      if (succeededCount > 0) {
        enqueueSnackbar(
          t(
            "{count, plural, one {Supplier added to organisation} other {# suppliers added to organisation}}",
            { ns: "SupplierNew", count: succeededCount }
          )
        );
      }

      if (failedSuppliers.length > 0) {
        enqueueSnackbar(
          t("Could not add: {suppliers}", {
            ns: "SupplierNew",
            suppliers: failedSuppliers
              .map(supplier => supplier.title)
              .join(", "),
          }),
          { variant: "error" }
        );
      }

      // Stay on the suppliers list; the successful subscriptions are kept and
      // handleComplete refetches the list once. Keep the modal open only when
      // nothing succeeded so the user can retry.
      if (succeededCount > 0) {
        await handleComplete(handleClose);
      }
    },
    [subscribeToOneSupplier, enqueueSnackbar, handleClose, handleComplete, t]
  );

  const initialValues: AddSupplierFormValues = {
    pimSuppliers: [],
  };
  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        pimSuppliers: Yup.array()
          .of(
            Yup.object({
              id: Yup.string().required(),
              title: Yup.string().required(),
            })
          )
          .label(t("Supplier", { ns: "Product" }))
          .min(1)
          .required(),
      }),
    [t]
  );

  const formId = React.useMemo(() => uniqueId(), []);

  return (
    <Formik<AddSupplierFormValues>
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
    >
      {({ values, setFieldValue, isSubmitting }) => (
        <Modal
          title={t("Add suppliers", { ns: "SupplierNew" })}
          handleClose={handleClose}
          actionButtons={[
            {
              label: t("Cancel", { ns: "Global" }),
              handleClick: handleClose,
              buttonProps: {
                variant: "text",
                disabled: isSubmitting,
              },
            },
            {
              label: t("Create", { ns: "Global" }),
              buttonProps: {
                type: "submit",
                form: formId,
                loading: isSubmitting,
                disabled: values.pimSuppliers.length === 0,
              },
            },
          ]}
        >
          <Form id={formId}>
            <Stack spacing={1}>
              <SupplierSelectMultiple
                label={t("Select existing suppliers", { ns: "SupplierNew" })}
                required={true}
                disabled={isSubmitting}
                value={values.pimSuppliers}
                onChange={suppliers => {
                  setFieldValue("pimSuppliers", suppliers);
                }}
              />
              <Box
                sx={{
                  paddingTop: 1,
                }}
              >
                <DialogContentText
                  sx={{
                    textAlign: "center",
                    marginBottom: 0,
                  }}
                >
                  <Box>
                    {t("Not finding your supplier?", {
                      ns: "OrganisationInvite",
                    })}
                  </Box>
                  <ModalOpenButton
                    Modal={RequestSupplierModal}
                    modalProps={{
                      title: t("Request creation of supplier", {
                        ns: "SupplierNew",
                      }),
                    }}
                  >
                    <Button color="secondary" size="small">
                      {t("Request a supplier", {
                        ns: "OrganisationInvite",
                      })}
                    </Button>
                  </ModalOpenButton>
                </DialogContentText>
              </Box>
            </Stack>
          </Form>
        </Modal>
      )}
    </Formik>
  );
};
